import React, { useEffect, useState, Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import useAnalysis from '../../hooks/useAnalysis';
import HumanModel from './HumanModel';
import LoadingSpinner from '../common/LoadingSpinner';
import UserChoices from './UserChoices';

const Analysis = ({ file, isInputVideo, modelPath, onAnalysisComplete, analysisResults, userChoices, onRedo, onRetry, onChoicesConfirm }) => {

  const { landmarks, worldLandmarks, isLoading, error } = useAnalysis(file, isInputVideo);
 
  const [analysisDone, setAnalysisDone] = useState(false);

  useEffect(() => {
    // Check if landmarks are present AND if we haven't already completed the analysis
    if (landmarks && worldLandmarks && !analysisDone) {
      console.log('Pose data extracted successfully!', { landmarks, worldLandmarks });
      onAnalysisComplete({ pose: landmarks, world: worldLandmarks });
      setAnalysisDone(true); // Set the flag to true to prevent future calls
      console.log('✅ Analysis logic completed. Flag set to true.');
    }
  }, [landmarks, worldLandmarks, onAnalysisComplete, analysisDone]);

  // Handle the confirmation of gender and height
  const handleUserChoicesConfirm = (choices) => {
    onChoicesConfirm(choices);
  };

  if (analysisResults && !userChoices) {
    // Show the height input and gender choice after analysis is complete
    return <UserChoices onChoicesConfirm={handleUserChoicesConfirm} />;
  }

  if (analysisResults && userChoices) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full p-4">
        <p className="mb-4 text-center">Your 3D model is ready!</p>
        <div className="w-full max-w-2xl h-96">
          <Canvas
            shadows
          >
            <PerspectiveCamera 
              makeDefault 
              position={[0, 1.5, 3]} 
              fov={40} 
            />
            
            <ambientLight intensity={0.5} />
            <directionalLight
              position={[5, 10, 7.5]}
              intensity={1}
              castShadow
              shadow-mapSize-width={1024}
              shadow-mapSize-height={1024}
              shadow-camera-far={20}
              shadow-camera-left={-10}
              shadow-camera-right={10}
              shadow-camera-top={10}
              shadow-camera-bottom={-10}
            />
            <hemisphereLight skyColor="#ffffff" groundColor="#000000" intensity={0.3} />

            <Suspense fallback={null}>
              <HumanModel modelPath={modelPath} poseLandmarks={analysisResults.pose} poseWorldLandmarks={analysisResults.world} realHeight={userChoices.height} castShadow receiveShadow />
            </Suspense>

            <OrbitControls
            enableZoom={true}
            enablePan={false}
            enableRotate={true}
            target={[0, 1, 0]}
            minPolarAngle={Math.PI / 6}
            maxPolarAngle={Math.PI - Math.PI / 6}
            minDistance={6}
            maxDistance={30}
            rotateSpeed={3.0}           // ← Added this
            zoomSpeed={1.5}             // ← Added this
            enableDamping={true}        // ← Added this
            dampingFactor={0.05}        // ← Added this
            />

            <mesh rotation={[0, 0, 0]} position={[0, -0.01, 0]} receiveShadow>
              <cylinderGeometry args={[2, 2, 0.1, 32]} />
              <meshStandardMaterial color="#A0A0A0" />
            </mesh>

          </Canvas>
        </div>
        <div className="mt-4 flex justify-center space-x-4">
          <button
            onClick={onRedo}
            className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 transition"
          >
            Redo
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 p-8">
        <p>Error: {error}</p>
        <p>Please try a different photo with a clear view of the body.</p>
        <div className="mt-4 flex justify-center space-x-4">
          <button
            onClick={onRedo}
            className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 transition"
          >
            Redo
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="text-center text-gray-500 p-8">
        <LoadingSpinner />
        <p>Analyzing and generating your 3D body...</p>
      </div>
    );
  }

  return (
    <div className="text-center text-gray-500 p-8">
      <p>Analysis is starting...</p>
    </div>
  );
};

export default Analysis;